import { useEffect, useRef, useState } from 'react'
import { Box, Button, Card, Loader, Segmented, Text, Title } from '@grovus/design-system'
import { AUTH_COPY, LANG_OPTIONS, isAuthErrorCode, type AuthErrorCode, type Dict, type Lang } from '../i18n'
import type { Theme } from '../lib/preferences'
import { AuthLayout } from './AuthLayout'
import { GoogleLogo } from './GoogleLogo'
import { startGoogleSignIn } from './api'
import { rememberSignInReturn } from './signInReturn'
import './google-button.css'

interface Props {
  t: Dict
  lang: Lang
  setLang: (lang: Lang) => void
  theme: Theme
  toggleTheme: () => void
  /** Why the boundary sent the person back here, as a kind rather than as text. */
  notice?: Notice
}

/**
 * What is wrong, not what to say about it. Storing the sentence instead would freeze the
 * language the error happened in: switching EN to RU afterwards redraws the screen around
 * an alert still in English, and a request that fails after the switch would answer in the
 * language it started under.
 */
export type Notice =
  | { kind: 'code'; code: AuthErrorCode }
  | { kind: 'expired' }

type Start =
  | { status: 'idle' }
  | { status: 'pending' }
  | { status: 'error'; notice: Notice }

function noticeText(notice: Notice, lang: Lang, t: Dict): string {
  return notice.kind === 'expired' ? t.authSessionExpired : AUTH_COPY[lang][notice.code]
}

/**
 * Reads and strips `?error=<code>` from the callback redirect (subtask 12.5). The code is
 * kept, not its translation: the URL is stripped once on mount and cannot be read again
 * when the language changes.
 */
function takeCallbackError(): AuthErrorCode | null {
  const url = new URL(window.location.href)
  const code = url.searchParams.get('error')
  if (code === null) return null
  url.searchParams.delete('error')
  window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`)
  return isAuthErrorCode(code) ? code : 'provider_error'
}

export function SignInScreen({ t, lang, setLang, theme, toggleTheme, notice }: Props) {
  const [start, setStart] = useState<Start>(() => (notice ? { status: 'error', notice } : { status: 'idle' }))
  const alertRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const code = takeCallbackError()
    if (code === null) return
    setStart({ status: 'error', notice: { kind: 'code', code } })
    // Runs once, reading the URL as it was on mount; a language change afterwards
    // should not re-read history that has already been stripped.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (start.status === 'error') alertRef.current?.focus()
  }, [start.status])

  async function onSignIn() {
    setStart({ status: 'pending' })
    const result = await startGoogleSignIn()
    if (result.status === 'ok') {
      rememberSignInReturn(window.location.pathname)
      window.location.assign(result.url)
      return
    }
    setStart({ status: 'error', notice: { kind: 'code', code: result.code } })
  }

  return (
    <AuthLayout
      topRight={(
        <>
          <Segmented
            value={lang}
            onChange={(value) => setLang(value as Lang)}
            data={LANG_OPTIONS}
          />
          <Button variant="default" onClick={toggleTheme}>
            {theme === 'light' ? t.dark : t.light}
          </Button>
        </>
      )}
    >
      <Card>
        <Title order={2} fz={18}>{t.authTitle}</Title>
        <Text fz="sm" mt={6} className="grv-muted">{t.authLede}</Text>

        <button
          type="button"
          className="google-signin-btn"
          style={{ marginTop: 18 }}
          disabled={start.status === 'pending'}
          aria-busy={start.status === 'pending'}
          onClick={onSignIn}
        >
          {start.status === 'pending' ? <Loader type="oval" size={18} color="currentColor" /> : <GoogleLogo />}
          <span>{t.authGoogleButton}</span>
        </button>
        <Text fz="xs" mt="sm" className="grv-muted">{t.authNote}</Text>

        {start.status === 'error' && (
          <Box
            ref={alertRef}
            role="alert"
            tabIndex={-1}
            mt="md"
            p="sm"
            style={{
              border: '1px solid light-dark(var(--mantine-color-bad-3), var(--mantine-color-bad-8))',
              borderRadius: 8,
              background: 'light-dark(var(--mantine-color-bad-0), var(--mantine-color-bad-9))',
              outline: 'none',
            }}
          >
            <Text fz="sm" style={{ color: 'light-dark(var(--mantine-color-bad-7), var(--mantine-color-bad-3))' }}>
              {noticeText(start.notice, lang, t)}
            </Text>
          </Box>
        )}
      </Card>
    </AuthLayout>
  )
}
